"use client";

import { ArrowRight } from "lucide-react";

import { useCheckout } from "@/lib/hooks/use-checkout";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

interface HeroCopyProps {
  headline: React.ReactNode;
  sub: React.ReactNode;
  className?: string;
  align?: "left" | "center";
}

export function HeroCopy({
  headline,
  sub,
  className,
  align = "center",
}: HeroCopyProps) {
  const { loadingPlan, error, start } = useCheckout("landing_hero");

  return (
    <div
      className={cn(
        "max-w-3xl",
        align === "center" && "mx-auto text-center",
        className,
      )}
    >
      <h1
        style={{ lineHeight: 1 }}
        className={cn(
          "font-heading font-extrabold tracking-[-0.035em] text-slate-900",
          "text-[36px] sm:text-[48px] lg:text-[60px]",
        )}
      >
        {headline}
      </h1>

      <p
        className={cn(
          "mt-5 text-[15px] leading-relaxed text-slate-600 sm:text-[17px]",
          align === "center" ? "mx-auto max-w-xl" : "max-w-xl",
        )}
      >
        {sub}
      </p>

      <div
        className={cn(
          "mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center",
          align === "center" && "sm:justify-center",
        )}
      >
        <button
          onClick={() => start("lifetime")}
          disabled={loadingPlan !== null}
          className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gradient-brand px-7 text-[14px] font-semibold text-white shadow-glow transition-all hover:bg-gradient-brand-hover hover:shadow-glow-hover hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loadingPlan === "lifetime" ? (
            <>
              <Spinner className="h-3.5 w-3.5" />
              Redirecting…
            </>
          ) : (
            <>
              Get Lifetime — £67
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </button>
        <a
          href="#pricing"
          className="inline-flex h-12 items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-6 text-[13.5px] font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
        >
          See plans ↓
        </a>
      </div>

      {error && (
        <p
          className={cn(
            "mt-4 text-[13px] text-rose-500",
            align === "center" && "text-center",
          )}
        >
          {error}
        </p>
      )}

      <p
        className={cn(
          "mt-5 text-[12px] text-slate-500",
          align === "center" && "text-center",
        )}
      >
        30-day refund · No recurring fees · Price locks in for life
      </p>
    </div>
  );
}
